import { expandSynonyms } from "@shared/lib/searchSynonyms";
import type { UserRole } from "@shared/lib/types";

export interface MatchableAction {
  roles: UserRole[];
  label: string;
  keywords: string[];
}

// Filters a list of quick actions by role and query, using the shared synonym
// map so "boo boo", "incidnet", or "make a new student" reach actions whose
// keywords don't literally contain those words.
//
// An action matches if ANY expanded query variant satisfies the per-word check
// (every token in the variant appears as a substring in some keyword or in
// the action's label). Returns the subset of `actions` that pass, preserving
// the input order.
export function matchActions<T extends MatchableAction>(
  actions: readonly T[],
  query: string,
  userRole: UserRole | undefined,
): T[] {
  if (!userRole) return [];
  const trimmed = query.trim();
  if (!trimmed) return [];

  const variantWordSets = expandSynonyms(trimmed)
    .map((v) => v.toLowerCase().split(/\s+/).filter(Boolean))
    .filter((words) => words.length > 0);
  if (variantWordSets.length === 0) return [];

  return actions.filter((a) => {
    if (userRole !== "admin" && !a.roles.includes(userRole)) return false;
    const labelLower = a.label.toLowerCase();
    return variantWordSets.some((words) =>
      words.every((word) =>
        a.keywords.some((kw) => kw.includes(word)) || labelLower.includes(word),
      ),
    );
  });
}
